import { useEffect, useState } from "react";
import {
  useStripe,
  useElements,
  PaymentElement,
  Elements,
} from "@stripe/react-stripe-js";
import {
  Appearance,
  loadStripe,
  StripeElementsOptions,
} from "@stripe/stripe-js";
import axios from "axios";
import { slackHook } from "src/utils/snug/slackHooks";
import { useRouter } from "src/hooks/use-router";
import { useAuth } from "src/hooks/use-auth";
import type { AuthContextType } from "src/contexts/auth/jwt-context";
import { SignupStepper } from "src/sections/starter-pay/signup-stepper";
import { writeStarterPayContext } from "src/sections/starter-pay/context";
import "./starter-pay.css";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "",
);

type PlanKey = "annual";

interface PlanConfig {
  label: string;
  amount: number;
  display: string;
  todayLabel: string;
  thereafter: string;
  bestFor: string;
  commitment: string;
  onboarding: string;
  onboardingQualifier: string;
  platform: string;
  platformQualifier: string;
  trust: string;
  will: string;
  promo: string;
  promoTerms: string;
  promoBadge: string;
}

const PLANS: Record<PlanKey, PlanConfig> = {
  annual: {
    label: "Starter",
    amount: 3000,
    display: "$3,000",
    todayLabel: "Annual fee",
    thereafter: "",
    bestFor:
      "Teams ready to commit and looking to maximize value from day one.",
    commitment: "Annual",
    onboarding: "Waived",
    onboardingQualifier: "",
    platform: "$3,000/yr",
    platformQualifier: "due today",
    trust: "$300",
    will: "$75",
    promo: "",
    promoTerms: "",
    promoBadge: "",
  },
};

const CUSTOM_PLAN_BASE: PlanConfig = {
  label: "Custom Plan",
  amount: 3000,
  display: "$3,000",
  todayLabel: "Onboarding fee",
  thereafter: "",
  bestFor: "Teams that want to get started before committing to a full year.",
  commitment: "Monthly",
  onboarding: "$3,000",
  onboardingQualifier: "first 3 months",
  platform: "$500/mo",
  platformQualifier: "starts month 4",
  trust: "$500",
  will: "$150",
  promo: "",
  promoTerms: "",
  promoBadge: "",
};

const CUSTOM_PARAMS = [
  "label",
  "billed",
  "today_label",
  "thereafter",
  "show_platform_qualifier",
  "onboarding",
  "platform",
  "trust",
  "will",
  "commitment",
  "promo",
  "promo_terms",
  "promo_badge",
] as const;

function formatFee(raw: string | null, fallback: string): string {
  if (raw == null) return fallback;
  const m = raw.match(/^\s*\$?\s*([\d,]+)(.*)$/);
  if (!m) return raw;
  const num = parseInt(m[1].replace(/,/g, ""), 10);
  if (!Number.isFinite(num)) return raw;
  return "$" + num.toLocaleString("en-US") + (m[2] || "");
}

function parseCustomPlan(): PlanConfig | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const hasAny = CUSTOM_PARAMS.some((p) => params.has(p));
  if (!hasAny) return null;
  const base = CUSTOM_PLAN_BASE;
  const billedDigits = (params.get("billed") || "").replace(/[^0-9]/g, "");
  const billed = billedDigits ? parseInt(billedDigits, 10) : 0;
  const hasBilled = billed > 0;
  return {
    label: params.get("label") || "Custom Plan",
    amount: hasBilled ? billed : base.amount,
    display: hasBilled ? "$" + billed.toLocaleString("en-US") : base.display,
    todayLabel: params.get("today_label") || base.todayLabel,
    thereafter: params.get("thereafter") || "",
    bestFor: base.bestFor,
    commitment: params.get("commitment") || base.commitment,
    onboarding: formatFee(params.get("onboarding"), base.onboarding),
    onboardingQualifier: base.onboardingQualifier,
    platform: formatFee(params.get("platform"), base.platform),
    platformQualifier:
      params.get("show_platform_qualifier") === "1"
        ? base.platformQualifier
        : "",
    trust: formatFee(params.get("trust"), base.trust),
    will: formatFee(params.get("will"), base.will),
    promo: params.get("promo") || "",
    promoTerms: params.get("promo_terms") || "",
    promoBadge: params.get("promo_badge") || "",
  };
}

const CUSTOM_PLAN: PlanConfig | null = parseCustomPlan();

const stripeAppearance: Appearance = {
  theme: "stripe",
  variables: {
    fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
    fontLineHeight: "1.5",
    borderRadius: "12px",
    colorBackground: "#F2F2EF",
    colorPrimary: "#3045C9",
    colorPrimaryText: "#0D0D12",
    colorText: "#0D0D12",
    colorTextSecondary: "#5A5A68",
    colorTextPlaceholder: "#8E8E9A",
    colorDanger: "#C93050",
    spacingUnit: "4px",
  },
  rules: {
    ".Block": {
      backgroundColor: "var(--colorBackground)",
      boxShadow: "none",
      padding: "12px",
    },
    ".Input": {
      padding: "12px 14px",
      backgroundColor: "#F2F2EF",
      border: "1px solid transparent",
      boxShadow: "none",
    },
    ".Input:focus": {
      backgroundColor: "#FFFFFF",
      border: "1px solid #3045C9",
      boxShadow: "0 0 0 3px rgba(48, 69, 201, 0.12)",
    },
    ".Tab": {
      padding: "10px 12px 8px 12px",
      border: "1px solid rgba(0,0,0,0.06)",
    },
    ".Tab--selected, .Tab--selected:focus, .Tab--selected:hover": {
      border: "1px solid #3045C9",
      backgroundColor: "#FFFFFF",
      boxShadow: "0 0 0 3px rgba(48, 69, 201, 0.12)",
    },
    ".Label": {
      fontWeight: "600",
      fontSize: "11px",
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: "#8E8E9A",
    },
  },
};

type PaymentSchedule = "single" | "split";

const isGetSnugDomain =
  window.location.hostname.endsWith("getsnug.com") ||
  window.location.hostname === "localhost";

function LockIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect
        x="3"
        y="7"
        width="10"
        height="7"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M5.5 7V5a2.5 2.5 0 015 0v2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CheckoutSection({
  name,
  email,
  plan,
  todayDisplay,
  isSplit,
  onSuccess,
}: {
  name: string;
  email: string;
  plan: PlanConfig;
  todayDisplay: string;
  isSplit: boolean;
  onSuccess: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setLoading(true);
    setError("");

    const { paymentIntent, error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
        payment_method_data: { billing_details: { name, email } },
      },
      redirect: "if_required",
    });

    if (stripeError) {
      setError(stripeError.message || "Payment failed. Please try again.");
      setLoading(false);
      return;
    }

    if (
      paymentIntent?.status === "succeeded" ||
      paymentIntent?.status === "processing"
    ) {
      slackHook(
        `🎉 GET STARTED PAYMENT ${paymentIntent.status.toUpperCase()}\n` +
          `Name: ${name}\nEmail: ${email}\n` +
          `Plan: ${plan.label} (${plan.display})\n` +
          `Schedule: ${isSplit ? `Split — ${todayDisplay} today, $1,500 in ~30 days` : `Single — ${todayDisplay}`}`,
      );
      onSuccess();
    } else {
      setError("Payment could not be completed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <>
      <div className="sp-card">
        <div className="sp-card-head">
          <h2 className="sp-section-title">Payment details</h2>
          <p className="sp-section-help">
            Securely processed by Stripe.{" "}
            {isSplit
              ? `Your card will be charged ${todayDisplay} today. The remaining $1,500 will be invoiced in ~30 days.`
              : "Your card is charged once for the amount shown below."}
          </p>
        </div>
        <div className="sp-stripe">
          <PaymentElement />
        </div>
      </div>

      {error && <div className="sp-error">{error}</div>}

      <button
        type="button"
        className="sp-cta"
        disabled={loading || !stripe}
        onClick={handleSubmit}
      >
        {loading ? (
          <span className="sp-cta-spinner" aria-hidden="true" />
        ) : (
          <LockIcon />
        )}
        Pay {todayDisplay} and get started
      </button>
    </>
  );
}

interface UserProfile {
  name: string;
  email: string;
  proGroupId: string;
}

export default function GetStartedPaymentPage() {
  const router = useRouter();
  const { verifyEmail } = useAuth<AuthContextType>();
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [intentLoading, setIntentLoading] = useState(false);
  const [intentError, setIntentError] = useState("");
  const [paymentSchedule, setPaymentSchedule] = useState<PaymentSchedule>("single");
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);

  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get("token");

  const apiBase = process.env.REACT_APP_SNUG_API_URL || "";
  const isCustom = CUSTOM_PLAN !== null;
  const plan: PlanConfig = isCustom ? (CUSTOM_PLAN as PlanConfig) : PLANS.annual;
  const isSplit = !isCustom && paymentSchedule === "split";
  const todayAmount = isSplit ? plan.amount / 2 : plan.amount;
  const todayDisplay = "$" + todayAmount.toLocaleString("en-US");

  // Fetch user data from the API using the stored access token
  const fetchProfile = async (): Promise<UserProfile | null> => {
    const accessToken = localStorage.getItem("s_at");
    if (!accessToken) return null;
    try {
      const { data } = await axios.get(`${apiBase}/v3/user-data/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { expand: "professional_group_role" },
      });
      const ud = data.data;
      return {
        name: ud?.full_name || "",
        email: ud?.contact_email || "",
        proGroupId: ud?.professional_group_role_user_data?.professional_group_id || "",
      };
    } catch {
      return null;
    }
  };

  // On mount: verify token if present, then fetch profile
  useEffect(() => {
    const init = async () => {
      if (token) {
        setVerifying(true);
        try {
          await verifyEmail({ token, password: null, first_name: null });
          const clean = window.location.pathname +
            (window.location.search.replace(/[?&]token=[^&]*/, "").replace(/^&/, "?") || "");
          window.history.replaceState({}, "", clean);
        } catch {
          setVerifyError("Could not verify your email. The link may have expired.");
          setVerifying(false);
          return;
        }
        setVerifying(false);
      }
      const p = await fetchProfile();
      setProfile(p);
      setProfileLoading(false);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create/recreate payment intent when profile is ready or schedule changes
  useEffect(() => {
    if (!profile?.name || !profile?.email) return;

    const createIntent = async () => {
      setClientSecret("");
      setIntentLoading(true);
      setIntentError("");
      try {
        const payload: Record<string, unknown> = {
          plan: isCustom ? "custom" : "annual",
          name: profile.name,
          email: profile.email,
          payment_schedule: isSplit ? "split" : "single",
          professional_group_id: profile.proGroupId,
        };
        if (isCustom) { payload.amount = plan.amount; payload.label = plan.label; }
        const { data } = await axios.post(`${apiBase}/v3/starter-pay/payment-intent/`, payload);
        setClientSecret(data.client_secret);
      } catch (e: any) {
        setIntentError(e?.response?.data?.error || "Could not initialize payment. Please try again.");
      } finally {
        setIntentLoading(false);
      }
    };

    createIntent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, paymentSchedule]);

  // Clean up custom plan URL params
  useEffect(() => {
    if (!CUSTOM_PLAN) return;
    const params = new URLSearchParams(window.location.search);
    let mutated = false;
    for (const p of CUSTOM_PARAMS) {
      if (params.has(p)) {
        params.delete(p);
        mutated = true;
      }
    }
    if (!mutated) return;
    const search = params.toString();
    window.history.replaceState(
      window.history.state,
      "",
      window.location.pathname +
        (search ? `?${search}` : "") +
        window.location.hash,
    );
  }, []);

  if (!isGetSnugDomain) return null;

  const tiers = isCustom
    ? [{ key: "custom", label: plan.label, config: plan, isActive: true }]
    : [
        {
          key: "annual",
          label: "Starter",
          config: PLANS.annual,
          isActive: true,
        },
      ];
  const activeTier = tiers[0];

  const planRows: { label: string; qualifier?: string; values: string[] }[] = [
    ...(isCustom
      ? [
          {
            label: "Onboarding fee",
            qualifier: activeTier.config.onboardingQualifier || undefined,
            values: tiers.map((t) => t.config.onboarding),
          },
        ]
      : []),
    {
      label: "Platform fee",
      qualifier: isSplit
        ? undefined
        : activeTier.config.platformQualifier || undefined,
      values: tiers.map((t) => t.config.platform),
    },
    { label: "Trust fee", values: tiers.map((t) => t.config.trust) },
    { label: "Will fee", values: tiers.map((t) => t.config.will) },
    ...(isCustom
      ? [{ label: "Commitment", values: tiers.map((t) => t.config.commitment) }]
      : []),
    {
      label: "Billed today",
      qualifier: isSplit ? "then $1,500 in ~30 days" : undefined,
      values: tiers.map((t) => (!isCustom ? todayDisplay : t.config.display)),
    },
  ];

  if (verifying) {
    return (
      <div className="sp-page">
        <div className="sp-brand">
          <img
            src="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/snug_logo.png"
            alt="Snug"
            className="sp-brand-logo"
          />
        </div>
        <section
          className="sp-inner"
          style={{ textAlign: "center", paddingTop: "4rem" }}
        >
          <span
            className="sp-cta-spinner"
            aria-label="Verifying email…"
            style={{ width: 32, height: 32, borderWidth: 3 }}
          />
          <p className="sp-sub" style={{ marginTop: "1rem" }}>
            Verifying your email…
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className="sp-page">
      <div className="sp-brand">
        <img
          src="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/snug_logo.png"
          alt="Snug"
          className="sp-brand-logo"
        />
      </div>

      <section className="sp-inner">
        <h1 className="sp-headline">
          Get started with <em>Snug.</em>
        </h1>
        <p className="sp-sub">
          {isCustom
            ? "Your custom plan is set up below. Review the details and complete your secure payment."
            : "Includes full platform access, white-label setup, and onboarding support."}
        </p>

        <SignupStepper currentStep="payment" />

        {verifyError && <div className="sp-error">{verifyError}</div>}

        <div className="sp-card sp-plan-card">
          <div className="sp-card-head">
            <span className="sp-card-eyebrow">Plan details</span>
          </div>
          <table className="sp-plan-table">
            <tbody>
              {planRows.map((row) => (
                <tr key={row.label}>
                  <th scope="row" className="sp-plan-row-label">
                    {row.label}
                    {row.qualifier && (
                      <span className="sp-plan-row-qualifier">
                        ({row.qualifier})
                      </span>
                    )}
                  </th>
                  {row.values.map((v, i) => (
                    <td
                      key={i}
                      className={`sp-plan-cell${tiers[i].isActive ? " is-active" : ""}`}
                    >
                      {v}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          {!isCustom && (
            <div className="sp-pay-schedule">
              <div
                className="sp-pay-schedule-options"
                role="radiogroup"
                aria-label="Payment schedule"
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={paymentSchedule === "single"}
                  className={`sp-pay-schedule-option${paymentSchedule === "single" ? " is-active" : ""}`}
                  onClick={() => setPaymentSchedule("single")}
                  disabled={profileLoading || intentLoading}
                >
                  1 payment
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={paymentSchedule === "split"}
                  className={`sp-pay-schedule-option${paymentSchedule === "split" ? " is-active" : ""}`}
                  onClick={() => setPaymentSchedule("split")}
                  disabled={profileLoading || intentLoading}
                >
                  2 payments
                </button>
              </div>
            </div>
          )}
        </div>

        {intentError && <div className="sp-error">{intentError}</div>}

        {(profileLoading || intentLoading) && (
          <div style={{ display: "flex", justifyContent: "center", padding: "2rem 0" }}>
            <span
              className="sp-cta-spinner"
              aria-label="Setting up your payment form…"
              style={{ width: 28, height: 28, borderWidth: 3 }}
            />
          </div>
        )}

        {clientSecret && !intentLoading && (
          <Elements
            stripe={stripePromise}
            options={
              {
                clientSecret,
                appearance: stripeAppearance,
                loader: "auto",
              } as StripeElementsOptions
            }
          >
            <CheckoutSection
              name={profile?.name || ""}
              email={profile?.email || ""}
              plan={plan}
              todayDisplay={todayDisplay}
              isSplit={isSplit}
              onSuccess={() => {
                writeStarterPayContext({
                  name: profile?.name || "",
                  company: "",
                  email: profile?.email || "",
                  plan: isCustom ? "custom" : "annual",
                  planLabel: plan.label,
                  paymentSchedule: isSplit ? "split" : "single",
                  proGroupId: profile?.proGroupId || "",
                });
                router.push("/starter-pay-onboarding");
              }}
            />
          </Elements>
        )}

        <p className="sp-disclaimer">
          Your card is processed securely by Stripe. We never store card details
          on our servers.
        </p>
      </section>
    </div>
  );
}
